import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import invariant from 'tiny-invariant'
import { Spin } from 'antd'

import { AuthUserParticipantContextProvider } from '../common/Participation'
import { ParticipationInvalidResult } from '../common/ParticipationInvalidResult'

import { useLoadDecision } from '../../redux/hooks'
import { useAccount, useAuth } from '../../context'
import { Participation } from '../common/Participation/Participation'
import { useResolvedParticipationSessionId } from '../../hooks/useResolvedParticipationSessionId'

export const ParticipationPage = (): ReactElement => {
  const { accountId } = useAccount()
  const { authUserId } = useAuth()
  const { decisionId, participationSessionId: idOrSlug } = useParams<'decisionId' | 'participationSessionId'>()
  invariant(decisionId, 'decisionId required in URL parameters')
  invariant(idOrSlug, 'participationSessionId required in URL parameters')

  // The route param may be a short slug; resolve it to a participation-session id first. This page
  // is only reachable when signed in, so the resolution query hits the authenticated endpoint and
  // has to identify the account & user (see useResolvedParticipationSessionId).
  const { participationSessionId, loading: resolvingSlug, notFound } = useResolvedParticipationSessionId(
    decisionId,
    idOrSlug,
    { accountId, authUserId }
  )
  const loading = useLoadDecision(accountId, decisionId)

  if (resolvingSlug || loading) return <Spin />
  if (notFound || !participationSessionId) {
    return <ParticipationInvalidResult subTitle="The link may have been replaced with a new one." />
  }

  return (
    <AuthUserParticipantContextProvider decisionId={decisionId}>
      <Participation decisionId={decisionId} participationSessionId={participationSessionId} />
    </AuthUserParticipantContextProvider>
  )
}
